/* Zooca first-party attribution v2. No checkout URLs or cart responses are stored. */
(function () {
  'use strict';
  if (window.ZoocaAttribution || window.Shopify?.designMode) return;
  const KEY = 'zooca.attribution.v2';
  const LEGACY_KEY = 'zooca.attribution.v1';
  const VERSION = '2';
  const TTL = 90 * 24 * 60 * 60 * 1000;
  const PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'campaign_id', 'ad_id', 'adset_id', 'fbclid', 'gclid'];
  const FIELDS = [...PARAMS, 'source', 'medium', 'landing', 'referrer', 'timestamp', 'source_origin', 'medium_origin'];
  const LEGACY_FIELDS = ['campaign', 'ad', 'adset', 'content', 'term', 'click_id'];
  let state = null, pending = null, visited = '', applied = '', flight = null;
  let loaded = false, revoked = false, flightBody = '';

  // Campaign metadata only: reject URLs, email-like strings, IPs and phone-like values.
  // Operators must never put personal data in campaign names or IDs.
  function clean(value) {
    if (typeof value !== 'string') return '';
    value = value.trim();
    if (value.length > 200 || !/^[\p{L}\p{N} _.,()+:-]*$/u.test(value) ||
        /(?:https?:|checkout|token|\b\d{1,3}(?:\.\d{1,3}){3}\b)/i.test(value) ||
        /^\+\d[\d ()-]{6,}$/.test(value)) return '';
    return value;
  }
  function landing(path) {
    // Store only public storefront paths; never account, app-proxy or checkout paths.
    if (path === '/') return '/';
    if (!/^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?(?:products|collections|pages|blogs)(?:\/[^/?#]+){0,3}\/?$/.test(path)) return '';
    try { return decodeURIComponent(path).split('/').every(part => !part || clean(part.replace(/-/g, '_'))) ? path.slice(0, 200) : ''; }
    catch (_) { return ''; }
  }
  function referrer(value) {
    try {
      const host = new URL(value).hostname;
      return /^(?:[a-z0-9-]+\.)+[a-z]{2,}$/i.test(host) && !/checkout|token/i.test(host) ? host : '';
    } catch (_) { return ''; }
  }
  // IDs are explicit URL parameters only. UTM names/content/terms are never IDs.
  // Normalized source/medium carry provenance; original UTM values stay separate.
  function normalize(p) {
    const marketing = PARAMS.some(key => Boolean(p[key]));
    const click = p.fbclid ? 'fbclid' : p.gclid ? 'gclid' : '';
    return {
      ...p,
      source: p.utm_source || (click === 'fbclid' ? 'facebook' : click === 'gclid' ? 'google' : marketing ? '' : 'direct'),
      medium: p.utm_medium || (click === 'fbclid' ? 'paid' : click === 'gclid' ? 'cpc' : marketing ? '' : 'none'),
      source_origin: p.utm_source ? 'utm_source' : click || (marketing ? 'unknown' : 'direct'),
      medium_origin: p.utm_medium ? 'utm_medium' : click || (marketing ? 'unknown' : 'direct')
    };
  }
  function capture() {
    const url = new URL(window.location.href);
    const p = {};
    PARAMS.forEach(key => { p[key] = clean(url.searchParams.get(key)); });
    const signature = JSON.stringify([landing(url.pathname), p]);
    if (signature === visited) return;
    visited = signature;
    const marketing = Object.values(p).some(Boolean);
    const touch = {
      ...normalize(p),
      landing: landing(url.pathname), referrer: referrer(document.referrer), timestamp: new Date().toISOString()
    };
    pending = { first: pending?.first || touch, last: marketing ? touch : pending?.last || null };
  }
  function allowed() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      const consent = privacy?.currentVisitorConsent();
      if (consent?.analytics !== 'yes' || consent?.marketing !== 'yes' ||
          privacy.analyticsProcessingAllowed() !== true || privacy.marketingAllowed() !== true) return false;
      if (window.Cookiebot && (window.Cookiebot.consent?.statistics !== true || window.Cookiebot.consent?.marketing !== true)) return false;
      return true;
    } catch (_) { return false; }
  }
  function validTouch(touch) {
    if (!touch || !Number.isFinite(Date.parse(touch.timestamp))) return null;
    const result = {};
    FIELDS.forEach(field => {
      result[field] = field === 'landing' ? landing(touch[field] || '') :
        field === 'referrer' ? referrer('https://' + (touch[field] || '')) :
        field === 'timestamp' ? new Date(touch.timestamp).toISOString() : clean(touch[field]);
    });
    const p = {};
    PARAMS.forEach(key => { p[key] = result[key]; });
    return { ...result, ...normalize(p) };
  }
  function collect() {
    capture();
    if (!allowed()) return false;
    revoked = false;
    if (!loaded) {
      loaded = true;
      try {
        // v1 collapsed originals and IDs; never invent v2 originals from that data.
        window.localStorage.removeItem(LEGACY_KEY);
        const stored = JSON.parse(window.localStorage.getItem(KEY));
        if (stored?.version === VERSION && stored.expires > Date.now() && stored.expires <= Date.now() + TTL) {
          state = { version: VERSION, expires: stored.expires, first: validTouch(stored.first), last: validTouch(stored.last) };
        }
      } catch (_) { /* Storage unavailable: retain consented attribution in memory. */ }
    }
    if (state && state.expires <= Date.now()) state = null;
    state ||= { version: VERSION, expires: Date.now() + TTL, first: null, last: null };
    state.first ||= pending?.first || null;
    if (pending?.last) state.last = pending.last;
    pending = null;
    try { window.localStorage.setItem(KEY, JSON.stringify(state)); } catch (_) { /* fail open */ }
    return true;
  }
  function attributes(clear) {
    const result = { __z_attr_version: clear ? '' : VERSION };
    ['ft', 'lt'].forEach((prefix, index) => {
      const touch = state?.[index === 0 ? 'first' : 'last'];
      FIELDS.forEach(field => { result['__z_' + prefix + '_' + field] = clear ? '' : touch?.[field] || ''; });
      // Clear retired v1 cart keys in the same request; never emit ambiguous aliases.
      LEGACY_FIELDS.forEach(field => { result['__z_' + prefix + '_' + field] = ''; });
    });
    return result;
  }
  function send(clear, force) {
    if (!clear && (!allowed() || !state)) return Promise.resolve();
    if (clear && !revoked) return Promise.resolve();
    const body = JSON.stringify({ attributes: attributes(clear) });
    if (flight) return body === flightBody ? flight : flight.then(() => send(clear, force));
    if (!force && body === applied) return Promise.resolve();
    flightBody = body;
    const controller = new AbortController();
    let timer;
    const request = Promise.resolve().then(() => window.fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js', {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body, signal: controller.signal, referrerPolicy: 'no-referrer'
    })).then(response => { if (response.ok) applied = body; }).catch(() => {});
    flight = Promise.race([request, new Promise(resolve => {
      timer = setTimeout(() => { controller.abort(); resolve(); }, 250);
    })]).finally(() => { clearTimeout(timer); flight = null; });
    return flight;
  }
  function refresh() {
    try { if (collect() && state.last) return send(false, false); } catch (_) { /* fail open */ }
    return Promise.resolve();
  }
  function consentChanged() {
    if (allowed()) { refresh(); return; }
    let denied = false;
    try {
      const consent = window.Shopify?.customerPrivacy?.currentVisitorConsent();
      denied = consent?.analytics === 'no' || consent?.marketing === 'no' ||
        window.Cookiebot?.consent?.statistics === false || window.Cookiebot?.consent?.marketing === false;
    } catch (_) {}
    if (!state && !loaded && !denied) return;
    // Removal is permitted on withdrawal; never read attribution without consent.
    revoked = true;
    state = null; pending = null; loaded = false; visited = '';
    try { window.localStorage.removeItem(KEY); window.localStorage.removeItem(LEGACY_KEY); } catch (_) { /* fail open */ }
    send(true, false);
  }
  async function beforeCheckout() {
    try {
      if (!collect()) return;
      // Share an active landing write, but refresh a previously completed write.
      const write = send(false, true);
      let timer;
      await Promise.race([write, new Promise(resolve => { timer = setTimeout(resolve, 300); })]);
      clearTimeout(timer);
    } catch (_) { /* Attribution can never block checkout. */ }
  }
  window.ZoocaAttribution = Object.freeze({ beforeCheckout, refresh });

  // Delegation survives section replacement. Replay preserves existing link handlers.
  const replaying = new WeakSet(), busy = new WeakSet();
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (link && window.ZoocaTrialCheckout?.handlesLink(link)) return;
    if (!link || replaying.has(link) || event.defaultPrevented || event.button !== 0 ||
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download') ||
        (link.target && link.target !== '_self') || !allowed()) return;
    let url;
    try { url = new URL(link.href, window.location.href); } catch (_) { return; }
    if (url.origin !== window.location.origin || !/\/(?:tools\/recurring\/checkout_link|checkout)\/?$/.test(url.pathname)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (busy.has(link)) return;
    busy.add(link);
    beforeCheckout().finally(() => {
      busy.delete(link); replaying.add(link);
      try { link.click(); } finally { replaying.delete(link); }
    });
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (window.ZoocaTrialCheckout) return;
    if (replaying.has(form) || event.defaultPrevented || !allowed() || event.submitter?.name !== 'checkout') return;
    let url;
    try { url = new URL(form.action, window.location.href); } catch (_) { return; }
    if (url.origin !== window.location.origin || !/\/cart\/?$/.test(url.pathname)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (busy.has(form)) return;
    busy.add(form);
    const submitter = event.submitter;
    beforeCheckout().finally(() => {
      busy.delete(form); replaying.add(form);
      try { form.requestSubmit(submitter); } finally { replaying.delete(form); }
    });
  }, true);
  document.addEventListener('visitorConsentCollected', consentChanged);
  ['CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline'].forEach(name => window.addEventListener(name, consentChanged));
  ['pageshow', 'popstate'].forEach(name => window.addEventListener(name, refresh));
  document.addEventListener('shopify:section:load', refresh);
  // Custom section routers may explicitly call refresh; History API navigation is also observed.
  ['pushState', 'replaceState'].forEach(name => {
    const original = window.history[name];
    window.history[name] = function () { const result = original.apply(this, arguments); refresh(); return result; };
  });
  refresh();
  try {
    window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], refresh);
  } catch (_) { /* Missing privacy API means no tracking. */ }
})();
